import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Input, SkeletonList, StatusBadge, buttonVariants } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { FilterToolbar } from "@/components/layout/FilterToolbar";
import { portalPackagesApi } from "../api/packages";
// Dates go through the PURE formatters with an EXPLICIT null zone, never the
// staff date-formatter hook in `@/utils/datetime` — that hook reads the staff
// AuthContext, which this tree never mounts. See portal/isolation.test.ts
// (FORBIDDEN_PREFIXES) and portal/portalInstantsRenderInOneZone.test.tsx.
import { formatCalendarDate, formatDateInTz } from "@/design-system/lib/format";
import {
  isRecipientActionable,
  nextOutstandingItem,
} from "../lib/completion";
import { itemActionFor, staffNoteFor } from "../lib/itemStatus";
import { StatusPill } from "../components/StatusPill";
import { PillToggle } from "../components/PillToggle";
import { PortalProgressBreakdown } from "../components/PortalProgressBreakdown";
import { useRequestsBadge } from "../components/PortalRequestsBadgeContext";
import { useRefetchOnVisible } from "../components/useRefetchOnVisible";
import type { CollectionItemStatus, PortalItemRowDto } from "../api/types";

// Post-Rollout PR 4 (PROB-28) — the Outstanding tab is now item-level.
// Each row is a single CollectionItem (template name, deal/package, state)
// rather than a package summary; rows drill DIRECTLY into the item
// fulfilment surface (PROB-28b) and are grouped by deal/package. The
// progress meter + Continue card layer on top in Commit E.

const STATUS_OPTIONS: Array<{ value: CollectionItemStatus; label: string }> = [
  { value: "Pending", label: "Not started" },
  { value: "InProgress", label: "In progress" },
  { value: "SubmittedForReview", label: "Under review" },
  { value: "ChangesRequested", label: "Needs changes" },
];

function itemHref(item: PortalItemRowDto): string {
  // PROB-28b — drill DIRECTLY to the item, never the package checklist page.
  return `/portal/packages/${item.packageId}/items/${item.id}`;
}

/**
 * "3 need you · 7 under review" — the same split the badge and the Home strip
 * read, from the same context, so all three agree by construction.
 *
 * This exists because buyer and seller badges now count `needsYou`: items
 * Phoenix is still reviewing no longer raise a badge, so arriving on this page
 * is the moment they have to become visible again. Without it, a recipient
 * whose items are all under review would find a page of rows and no
 * explanation of why nothing was flagged.
 */
function RequestsSplitHeader() {
  const { counts } = useRequestsBadge();
  const { needsYou, underReview } = counts;
  if (needsYou <= 0 && underReview <= 0) return null;

  const parts: string[] = [];
  if (needsYou > 0) parts.push(`${needsYou} need${needsYou === 1 ? "s" : ""} you`);
  if (underReview > 0) parts.push(`${underReview} under review`);

  return (
    <p className="text-small text-gray-mid m-0" data-testid="requests-split-header">
      {parts.join(" · ")}
    </p>
  );
}

export function RequestsTab() {
  // The table shows the Requests (in-flight) bucket; the progress meter
  // counts ALL items (requests ∪ documents) so already-approved items
  // count toward the denominator — otherwise the meter undercounts and the
  // PROB-11 contradiction returns.
  const [rows, setRows] = useState<PortalItemRowDto[] | null>(null);
  const [allItems, setAllItems] = useState<PortalItemRowDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (isCancelled?: () => boolean) => {
    const [reqs, docs] = await Promise.all([
      portalPackagesApi.listMyItems("requests"),
      portalPackagesApi.listMyItems("documents"),
    ]);
    if (isCancelled?.()) return;
    setRows(reqs);
    setAllItems([...reqs, ...docs]);
    setError(null);
  }, []);

  const refetch = useCallback(async () => {
    try {
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load requests.");
    }
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await load(() => cancelled);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load requests.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  useRefetchOnVisible(refetch);

  const [statusFilter, setStatusFilter] = useState<Set<CollectionItemStatus>>(new Set());
  const [dealFilter, setDealFilter] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");

  // Distinct deal/package options for the deal filter chips.
  const dealOptions = useMemo(() => {
    if (!rows) return [];
    const seen = new Map<string, string>();
    for (const r of rows) if (!seen.has(r.packageId)) seen.set(r.packageId, r.packageLabel);
    return [...seen.entries()].map(([id, label]) => ({ id, label }));
  }, [rows]);

  const filtered = useMemo<PortalItemRowDto[] | null>(() => {
    if (!rows) return null;
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter.size > 0 && !statusFilter.has(r.status)) return false;
      if (dealFilter.size > 0 && !dealFilter.has(r.packageId)) return false;
      if (term && !r.templateName.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [rows, statusFilter, dealFilter, search]);

  // Per-group progress for the collapsible group headers ("2 of 3
  // submitted"), computed over the full filtered set so it doesn't change
  // page-to-page. This is the Outstanding table, which only holds in-flight
  // items (never Approved/Waived), so "done" here means the recipient has
  // submitted it and is no longer on the hook — i.e. not actionable.
  const groupMeta = useMemo(() => {
    const m = new Map<string, { label: string; done: number; total: number }>();
    for (const r of filtered ?? []) {
      const g = m.get(r.packageId) ?? { label: r.packageLabel, done: 0, total: 0 };
      g.total += 1;
      if (!isRecipientActionable(r)) g.done += 1;
      m.set(r.packageId, g);
    }
    return m;
  }, [filtered]);

  // The next recipient-actionable item PER package, so each group header's
  // Continue button points at that package's own next step (computed over
  // the full filtered set, not the current page).
  const groupNext = useMemo(() => {
    const byPkg = new Map<string, PortalItemRowDto[]>();
    for (const r of filtered ?? []) {
      const arr = byPkg.get(r.packageId) ?? [];
      arr.push(r);
      byPkg.set(r.packageId, arr);
    }
    const m = new Map<string, PortalItemRowDto>();
    for (const [key, arr] of byPkg) {
      const n = nextOutstandingItem(arr);
      if (n) m.set(key, n);
    }
    return m;
  }, [filtered]);

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert">
        {error}
      </p>
    );
  }
  if (!rows) {
    return (
      <SkeletonList data-testid="requests-loading" />
    );
  }

  const toggle = <T extends string>(
    setter: React.Dispatch<React.SetStateAction<Set<T>>>,
    value: T,
  ) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });

  return (
    <section aria-label="Requests" className="flex flex-col gap-space-5">
      <RequestsSplitHeader />
      <DashboardSummary all={allItems} outstanding={rows} />
      <NeedsChangesSurface items={filtered ?? []} />

      {/* Filter bar and table are grouped with a tight gap so the
          search/filter card sits close to the table it controls. */}
      <div className="flex flex-col gap-space-3">
        <FilterToolbar
          className="mb-0"
          searchInput={
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title…"
              data-testid="requests-filter-search"
            />
          }
          filters={[
          {
            key: "status",
            label: "Status",
            control: (
              <PillToggle
                options={STATUS_OPTIONS}
                selected={statusFilter}
                onToggle={(v) => toggle(setStatusFilter, v)}
                testIdPrefix="requests-filter-status"
              />
            ),
          },
          ...(dealOptions.length > 1
            ? [
                {
                  key: "deal",
                  label: "Deal / Package",
                  control: (
                    <PillToggle
                      options={dealOptions.map((d) => ({ value: d.id, label: d.label }))}
                      selected={dealFilter}
                      onToggle={(v) => toggle(setDealFilter, v)}
                      testIdPrefix="requests-filter-deal"
                    />
                  ),
                },
              ]
            : []),
        ]}
      />

      <DataTable<PortalItemRowDto>
        rows={filtered ?? []}
        rowKey={(r) => r.id}
        sortable
        defaultSort={{ key: "updated", dir: "desc" }}
        pagination={{ pageSize: 25 }}
        groupBy={(r) => r.packageId}
        groupLabel={(key) => {
          const g = groupMeta.get(key);
          return (
            <span>
              {g?.label ?? "—"}{" "}
              <span className="font-normal normal-case tracking-normal text-gray-mid">
                ({g?.done ?? 0} of {g?.total ?? 0} submitted)
              </span>
            </span>
          );
        }}
        groupActions={(key) => {
          // Per-package CTAs in the group header: the checklist for THIS
          // package, and Continue into its own next actionable item.
          const next = groupNext.get(key);
          return (
            <div className="flex items-center gap-space-2">
              <Link
                to={`/portal/packages/${key}`}
                className={cn(buttonVariants({ variant: "secondary", size: "sm" }), "no-underline")}
                data-testid={`requests-checklist-link-${key}`}
              >
                View checklist
              </Link>
              {next && (
                <Link
                  to={itemHref(next)}
                  className={cn(buttonVariants({ variant: "primary", size: "sm" }), "no-underline")}
                  data-testid={`requests-continue-cta-${key}`}
                >
                  Continue
                </Link>
              )}
            </div>
          );
        }}
          emptyMessage={
            rows.length === 0
              ? "Nothing outstanding right now. We'll email you when something new arrives."
              : "No items match those filters."
          }
          columns={requestsColumns()}
        />
      </div>
    </section>
  );
}

function requestsColumns(): Column<PortalItemRowDto>[] {
  return [
    {
      key: "title",
      header: "Title",
      sortable: true,
      sortAccessor: (r) => r.templateName,
      render: (r) => <RequestTitleCell row={r} />,
    },
    {
      key: "deal",
      header: "Deal / Package",
      // Hidden on phones — rows are grouped by package, so the group
      // header already carries this.
      mobileHidden: true,
      sortable: true,
      sortAccessor: (r) => r.packageLabel,
      render: (r) => (
        <span className="text-caption text-gray-mid">
          {r.packageLabel}
          {r.dealNumber ? ` · ${r.dealNumber}` : ""}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      sortAccessor: (r) => r.status,
      render: (r) => (
        <StatusPill
          status={r.status}
          approvalChain={r.approvalChain}
          data-testid={`request-chip-${r.id}`}
        />
      ),
    },
    {
      key: "due",
      header: "Due",
      sortable: true,
      sortAccessor: (r) => (r.dueDate ? Date.parse(r.dueDate) : Number.POSITIVE_INFINITY),
      render: (r) => (
        <span className="text-caption text-gray-mid">
          {r.dueDate ? formatCalendarDate(r.dueDate) : "—"}
        </span>
      ),
    },
    {
      key: "updated",
      header: "Last updated",
      mobileHidden: true,
      sortable: true,
      sortAccessor: (r) => Date.parse(r.lastUpdatedUtc),
      render: (r) => <UpdatedAtCell at={r.lastUpdatedUtc} />,
    },
    {
      key: "action",
      header: "Action",
      render: (r) => {
        const action = itemActionFor(r.status, r.approvalChain);
        const tone =
          action.tone === "destructive"
            ? "text-status-error"
            : action.tone === "secondary"
              ? "text-gray-mid"
              : "text-onyx";
        return (
          <Link
            to={itemHref(r)}
            className={`text-small font-medium underline hover:text-gold-shadow ${tone}`}
            data-testid={`request-cta-${r.id}`}
          >
            {action.label}
          </Link>
        );
      },
    },
  ];
}

function RequestTitleCell({ row }: { row: PortalItemRowDto }) {
  const staffNote = staffNoteFor(row);
  return (
    <div className="min-w-0">
      <Link
        to={itemHref(row)}
        className="text-small font-medium text-onyx hover:text-gold-shadow"
        data-testid={`request-title-${row.id}`}
      >
        {row.templateName}
      </Link>
      {staffNote && (
        <p className="text-caption text-status-error m-0 mt-space-1" data-testid={`request-note-${row.id}`}>
          {staffNote}
        </p>
      )}
    </div>
  );
}

function UpdatedAtCell({ at }: { at: string }) {
  return <span className="text-caption text-gray-mid">{formatDateInTz(at, null)}</span>;
}

// Top-of-dashboard progress meter. The meter is a segmented status breakdown
// over every item (requests ∪ documents) so the recipient sees each status,
// not a single number that hides "under review" inside "complete". The
// per-package Continue / View-checklist CTAs now live in each table group
// header (relative to that package). When nothing needs the recipient the
// block adds an all-caught-up message below the breakdown — items still
// under review with staff don't block that state.
function DashboardSummary({
  all,
  outstanding,
}: {
  all: PortalItemRowDto[];
  outstanding: PortalItemRowDto[];
}) {
  const next = nextOutstandingItem(outstanding);

  if (all.length === 0) return null;

  return (
    <div
      className="flex flex-col gap-space-4"
      data-testid={next ? "requests-summary" : "requests-all-done"}
    >
      <PortalProgressBreakdown items={all} />
      {!next && (
        <div className="p-space-4 border border-status-success bg-status-success/10 rounded-md">
          <h3 className="text-h5 font-display text-onyx m-0">You're all caught up.</h3>
          <p className="text-caption text-gray-mid mt-space-1">
            We'll email you if we need anything else.
          </p>
        </div>
      )}
    </div>
  );
}

// PROB-28 "Needs changes" loud surface. Items the reviewer bounced back
// pin to the top with the reviewer's note + a single Resubmit CTA so the
// recipient never has to hunt for why something came back. Review loop — an
// item stays here while its response is owed, including after the first edit
// moves it to InProgress.
function NeedsChangesSurface({ items }: { items: PortalItemRowDto[] }) {
  const attention = items.filter(
    // A chain item never joins: a chain return is not a staff return owed a
    // response (it stops at Phoenix until staff revise it).
    (i) => !i.approvalChain && (i.status === "ChangesRequested" || i.responseOwed === true),
  );
  if (attention.length === 0) return null;
  return (
    <div
      className="p-space-4 border border-status-warning bg-status-warning/5 rounded-md"
      data-testid="attention-zone"
    >
      <h3 className="text-h5 font-display text-onyx m-0 flex items-center gap-space-2">
        <StatusBadge tone="warn">Needs changes</StatusBadge>
        Action needed on {attention.length} {attention.length === 1 ? "item" : "items"}
      </h3>
      <ul className="mt-space-3 flex flex-col gap-space-2 list-none p-0">
        {attention.map((item) => {
          const note = staffNoteFor(item);
          return (
            <li
              key={`attn-${item.id}`}
              className="flex items-center justify-between gap-space-3 p-space-3 border border-border-light rounded-md bg-white-pure"
            >
              <div className="min-w-0">
                <p className="text-small font-medium text-onyx m-0">{item.templateName}</p>
                {note && (
                  <p
                    className="text-caption text-gray-mid m-0 mt-space-1"
                    data-testid={`attention-note-${item.id}`}
                  >
                    {note}
                  </p>
                )}
              </div>
              <Link
                to={itemHref(item)}
                className={cn(buttonVariants({ variant: "primary", size: "sm" }), "shrink-0 no-underline")}
                data-testid={`attention-cta-${item.id}`}
              >
                Fix &amp; resubmit
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
